import { Company } from '../services/company.service';

export interface SetupTask {
  id: string;
  title: string;
  description: string;
  routerLink: string;
  queryParams: Record<string, string>;
  fragment?: string;
  required?: boolean;
  /** Show the "Erledigt" button only when no technical measurement is possible. */
  allow_manual_confirmation?: boolean;
  isCompleted: (company: Company, productCount: number) => boolean;
  isVisible?: (company: Company) => boolean;
}

const notMember = (c: Company) => !c.is_member;
const onlyMember = (c: Company) => !!c.is_member;

export const ALL_SETUP_TASKS: SetupTask[] = [
  // 1. Required: nothing works without payment
  {
    id: 'stripe',
    title: 'Zahlungen einrichten',
    description: 'Richte deine Zahlungsabwicklung ein, damit Kunden Gutscheine kaufen können.',
    routerLink: '/settings',
    queryParams: { tab: 'payments' },
    required: true,
    isCompleted: (c) => !!c.is_stripe_onboarding_success,
    // Members have no Stripe account: their network pays them by bank transfer.
    isVisible: notMember,
  },
  // 1b. Required for members: the network pays them by bank transfer
  {
    id: 'member-bank-account',
    title: 'Bankverbindung hinterlegen',
    description:
      'Dein Netzwerk überweist dir deine Einlösungen einmal im Monat. Dafür braucht es deine IBAN.',
    routerLink: '/settings',
    queryParams: { tab: 'bank-account' },
    required: true,
    isVisible: onlyMember,
    isCompleted: (c) => !!c.has_bank_account,
  },
  // 2. Required: app must be embedded to be reachable (not relevant for member accounts)
  {
    id: 'integration',
    title: 'Integration einrichten',
    description: 'Binde deine Gutschein-App direkt in deine bestehende Webseite ein.',
    routerLink: '/settings',
    queryParams: { tab: 'integration' },
    required: true,
    // Detection needs a visitor on the website, and a website saved in the settings.
    allow_manual_confirmation: true,
    isVisible: notMember,
    isCompleted: (c) => !!c.integration_detected_at,
  },
  // 3. Core shop content
  {
    id: 'app-config',
    title: 'Gutschein-App konfigurieren',
    description: 'Gib deiner App einen Titel, eine Beschreibung und einen Mindestbetrag.',
    routerLink: '/settings',
    queryParams: { tab: 'sell-settings' },
    fragment: 'section-app-config',
    isVisible: notMember,
    isCompleted: (c) => !!c.app_config_customized,
  },
  // 4. Branding
  {
    id: 'logo',
    title: 'Logo hochladen',
    description: 'Füge dein Unternehmenslogo hinzu, damit dein Shop wiedererkennbar ist.',
    routerLink: '/settings',
    queryParams: { tab: 'sell-settings' },
    fragment: 'section-logo',
    isVisible: notMember,
    isCompleted: (c) => !!c.logo_image_path,
  },
  {
    id: 'banner',
    title: 'Banner hochladen',
    description: 'Ein ansprechendes Bannerbild macht deinen Gutschein-Shop attraktiver.',
    routerLink: '/settings',
    queryParams: { tab: 'sell-settings' },
    fragment: 'section-banner',
    isVisible: notMember,
    isCompleted: (c) => !!c.banner_image_path,
  },
  {
    id: 'shop-description',
    title: 'Shopbeschreibung hinzufügen',
    description: 'Beschreibe dein Unternehmen und was dich besonders macht.',
    routerLink: '/settings',
    queryParams: { tab: 'storefront' },
    fragment: 'section-description',
    isVisible: notMember,
    isCompleted: (c) => !!c.description,
  },
  // A new id on purpose (2026-10-19): the old "voucher-design" task completed on the
  // app's primary colour, so most companies had it ticked off; every company should
  // see this one once, as the announcement of the design editor.
  {
    id: 'custom-voucher-design',
    title: 'Eigenes Gutschein-Design gestalten',
    description:
      'Gestalte deine Gutschein-PDFs im Editor: eigener Hintergrund, Farben, Schriften und Texte, die zu deinem Geschäft passen.',
    routerLink: '/settings',
    queryParams: { tab: 'voucher-design' },
    fragment: 'section-design-library',
    isVisible: notMember,
    // Set by the backend once a design was changed in the editor or uploaded;
    // picking or activating a template is not that.
    isCompleted: (c) => !!c.voucher_design_edited,
  },
  // 4b. Members: their logo shows next to them in the network's member list
  {
    id: 'member-logo',
    title: 'Logo hochladen',
    description: 'Dein Logo erscheint bei dir in der Mitgliederliste deines Netzwerks.',
    routerLink: '/settings',
    queryParams: { tab: 'company' },
    fragment: 'section-member-logo', // #section-logo is the Gutschein-App tab's, also in the page
    isVisible: onlyMember,
    isCompleted: (c) => !!c.logo_image_path,
  },
  // 5. Operations
  {
    id: 'redemption-pin',
    title: 'Einlösungs-PIN festlegen',
    description: 'Aktiviere die Einlösung von Gutscheinen per PIN für deine Mitarbeiter.',
    routerLink: '/settings',
    queryParams: { tab: 'fees-redemption' },
    fragment: 'section-redemption-pin',
    // A network redeems nothing; its members set their own PIN.
    isVisible: (c) => !c.is_group_admin,
    isCompleted: (c) => !!c.redemption_pin,
  },
  // 5b. Members: sell the network's vouchers right on their own website
  {
    id: 'member-integration',
    title: 'Netzwerk-Gutscheine auf deiner Webseite',
    description:
      'Binde den Gutschein-Shop deines Netzwerks in deine Webseite ein, damit deine Kunden die Gutscheine direkt bei dir finden.',
    routerLink: '/settings',
    queryParams: { tab: 'integration' },
    allow_manual_confirmation: true,
    isVisible: onlyMember,
    isCompleted: (c) => !!c.integration_detected_at,
  },
  // 6. Promotion
  {
    id: 'qr-code-print',
    title: 'QR-Code ausdrucken',
    description:
      'Platziere deinen QR-Code an der Kasse oder im Schaufenster, damit Laufkunden dein Gutschein-Angebot entdecken.',
    routerLink: '/settings',
    queryParams: { tab: 'qr-code' },
    allow_manual_confirmation: true,
    isVisible: notMember,
    isCompleted: () => false,
  },
  // 7. Advanced / optional features
  {
    id: 'products-activate',
    title: 'Produkt-Gutscheine aktivieren',
    description: 'Verkaufe Gutscheine für konkrete Angebote wie Massagen, Menüs oder Kurse.',
    routerLink: '/products',
    queryParams: {},
    fragment: 'section-products-activate',
    isVisible: notMember,
    isCompleted: (c) => !!c.voucher_products_enabled,
  },
  {
    id: 'products-first',
    title: 'Erstes Produkt erstellen',
    description: 'Lege dein erstes Angebot an, das Kunden als Gutschein verschenken können.',
    routerLink: '/products',
    queryParams: {},
    fragment: 'section-products-create',
    isVisible: (c) => !c.is_member && !!c.voucher_products_enabled,
    isCompleted: (_c, productCount) => productCount > 0,
  },
];
